import type { Dictionary } from './pt-BR';

/**
 * Estrutura en-US.
 *
 * `DeepPartial<Dictionary>` de propósito: o resolvedor (`index.ts`) cai de volta
 * no pt-BR chave a chave, então um idioma parcialmente traduzido nunca produz
 * texto faltando na tela. O que está aqui é o esqueleto navegacional — o corpo
 * regulado (significados de assinatura, rótulos de situação) só deve ser
 * traduzido depois de revisão do responsável pela qualidade, porque esses
 * textos aparecem em registro assinado.
 */
/**
 * As folhas viram `string`, e não o literal de origem. `ptBR` é declarado
 * `as const` (pt-BR.ts:162), então `Dictionary['common']['save']` é o tipo
 * `'Salvar'` — literalmente. Sem este alargamento, traduzir é impossível por
 * construção: `save: 'Save'` não é atribuível a `'Salvar'` e o `tsc` do
 * `pnpm --filter @qualicloud/web build` reprova o pacote inteiro.
 *
 * O que a estrutura precisa garantir é a FORMA das chaves — para que uma chave
 * removida no pt-BR quebre a tradução em compilação. O texto é justamente o que
 * deve variar.
 */
export type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends string
    ? string
    : T[K] extends Record<string, unknown>
      ? DeepPartial<T[K]>
      : T[K];
};

export const enUS: DeepPartial<Dictionary> = {
  common: {
    appName: 'QualiCloud eQMS',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    clearFilters: 'Clear filters',
    export: 'Export',
    print: 'Print',
    download: 'Download',
    edit: 'Edit',
    remove: 'Remove',
    add: 'Add',
    create: 'Create',
    view: 'View',
    loading: 'Loading…',
    retry: 'Try again',
    required: 'required',
    optional: 'optional',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    none: 'None',
    of: 'of',
    actions: 'Actions',
    details: 'Details',
    unknown: 'Not provided',
  },

  nav: {
    dashboard: 'Dashboard',
    masterList: 'Master document list',
    approvals: 'My approvals',
    documentTypes: 'Document types',
    auditTrail: 'Audit trail',
    organization: 'Organization',
    users: 'Users',
    departments: 'Departments',
    notifications: 'Notifications',
    quality: 'Quality',
    administration: 'Administration',
    skipToContent: 'Skip to main content',
  },

  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    username: 'Username',
    password: 'Password',
    organization: 'Organization',
    mfaCode: 'Verification code',
    changePassword: 'Change password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Confirm new password',
    passwordStrength: 'Password strength',
  },
};
